import { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardProps,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  styled,
} from '@mui/material';

import { updatePin } from 'store/slices/userSlice';

import { useAppDispatch } from 'store/hooks';
import { AuthContext } from 'utils/AuthContext';
import PinInput from 'react-pin-input';
import { toast } from 'react-toastify';
import ChangePassword from 'components/ChangePassword';
import { StyledDialog } from 'theme/index';
import CloseIcon from '@mui/icons-material/Close';

const Account = (props: any) => {
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthContext);
  const patientId = props?.from === 'admin' ? props?.patientId : user?.patientId;
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [pin, setPin] = useState<string>();
  const [showPinConfirm, setShowPinConfirm] = useState<boolean>(false);
  const [confirmPin, setConfirmPin] = useState<string>();
  const [error, setError] = useState<string>('');
  const [disableNextButton, setDisableNextButton] = useState<boolean>(true);
  const [disableConfirmButton, setDisableConfirmButton] = useState<boolean>(true);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState<boolean>(false);
  let ele: PinInput | null;
  const StyleCard = styled(Card)<CardProps>(({ theme }) => ({
    boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
    border: '1px solid #EFF4FF',
    borderRadius: 12,
    background: '#FFF',
    margin: '12px 16px',
    position: 'relative',
    zIndex: 99,
    padding: 20,
    '& .MuiTypography-root': {
      position: 'relative',
    },
    '& .MuiCardContent-root:last-child': {
      padding: 0,
    },
    /**
     * MM-1132 feedback: tile titles are 17px (was 20px).
     */
    '.MuiTypography-h2': {
      fontSize: 17,
    },
  }));

  const handleNext = () => {
    setShowPinConfirm(true);
    if (ele) {
      ele.clear();
    }
  };

  const handleConfirmPin = () => {
    if (pin === confirmPin) {
      const data = { pin };
      dispatch(updatePin(data))
        .unwrap()
        .then((response: any) => {
          if (response?.status === 200) {
            toast('PIN updated successfully!', {
              type: 'success',
              icon: true,
            });
            setShowDialog(false);
            setShowPinConfirm(false);
          }
        });
    } else {
      setError('PIN does not match');
      setTimeout(() => {
        setError('');
      }, 2000);
      if (ele) {
        ele.clear();
      }
    }
  };

  const handleOnComplete = (value: string) => {
    if (!showPinConfirm) {
      setPin(value);
      setDisableNextButton(value.length !== 6);
    } else {
      setConfirmPin(value);
      setDisableConfirmButton(value.length !== 6);
    }
  };
  const handleBack = () => {
    setShowPinConfirm(false);
    setShowDialog(true);
    setError('');
    if (ele) {
      ele.clear();
    }
  };

  const handlePinChange = (val: any) => {
    if (val.length < 6) {
      setDisableNextButton(true);
      setDisableConfirmButton(true);
    }
  };

  return (
    <Container component='main' maxWidth='md'>
      <Grid container>
        <Grid item xs={10} display={'flex'} alignItems={'center'}>
          {/* MM-1123: same top space above the heading as the Dashboard (paddingTop 6) */}
          <Typography variant='h5' gutterBottom sx={{ paddingTop: 6 }}>
            {/* MM-1123: Settings page (Change Password / Change PIN); Account Management moved to its own page */}
            Settings
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <StyleCard>
            <CardContent>
              <CardMedia
                component={'img'}
                src='/images/Group-9550-1.png'
                alt='card-img'
                sx={{ width: 389, height: 'auto', position: 'absolute', right: 0, top: 0 }}
              />
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'space-between'}
                  gap={1.5}
                >
                  <Typography variant='h2'>Change Your Password</Typography>
                  <Grid container>
                    <Grid item xs={12} sm>
                      <Typography variant='subtitle1'>
                        Protect your privacy: Change your password regularly.
                      </Typography>
                    </Grid>
                    {/* MM-1132 feedback: on phones the link goes under the text, like Request Your Balance */}
                    <Grid
                      item
                      xs={12}
                      sm='auto'
                      alignItems={'end'}
                      textAlign={'end'}
                      mt={{ xs: 1, sm: 0 }}
                      pl={{ sm: 2 }}
                    >
                      <Button
                        variant='text'
                        size='small'
                        sx={{ fontSize: '14px', padding: 0, whiteSpace: 'nowrap' }}
                        onClick={() => {
                          setShowChangePasswordDialog(true);
                        }}
                      >
                        CHANGE PASSWORD
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </StyleCard>
        </Grid>

        <Grid item xs={12}>
          <StyleCard>
            <CardContent>
              <CardMedia
                component={'img'}
                src='/images/Group-9550-1.png'
                alt='card-img'
                sx={{ width: 389, height: 'auto', position: 'absolute', right: 0, top: 0 }}
              />
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'space-between'}
                  gap={1.5}
                >
                  <Typography variant='h2'>Change Your PIN</Typography>
                  <Grid container>
                    <Grid item xs={12} sm>
                      <Typography variant='subtitle1'>
                        Keep your account secure by updating PIN.
                      </Typography>
                    </Grid>
                    {/* MM-1132 feedback: on phones the link goes under the text, like Request Your Balance */}
                    <Grid
                      item
                      xs={12}
                      sm='auto'
                      alignItems={'end'}
                      textAlign={'end'}
                      mt={{ xs: 1, sm: 0 }}
                      pl={{ sm: 2 }}
                    >
                      <Button
                        variant='text'
                        size='small'
                        sx={{ fontSize: '14px', padding: 0, whiteSpace: 'nowrap' }}
                        onClick={() => {
                          setShowDialog(true);
                        }}
                      >
                        UPDATE PIN
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </StyleCard>
        </Grid>
      </Grid>

      <StyledDialog aria-labelledby='simple-dialog-title' open={showDialog}>
        <DialogTitle id='simple-dialog-title' sx={{ padding: '16px 24px 0' }}>
          {showPinConfirm ? 'Confirm New PIN' : 'Set New PIN'}
          {patientId && (
            <IconButton
              aria-label='close'
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
              onClick={() => {
                setShowDialog(false);
                setShowPinConfirm(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid
            container
            spacing={0}
            direction='column'
            alignItems='center'
            justifyContent='center'
          >
            <Typography>
              {!showPinConfirm && showDialog
                ? 'Enter a new 6-digit PIN for your account. '
                : 'Re-enter your new 6-digit PIN to confirm'}
            </Typography>

            <Grid item xs={3} mt={'20px'}>
              <PinInput
                length={6}
                focus
                secret
                secretDelay={500}
                type='numeric'
                inputMode='numeric'
                onComplete={handleOnComplete}
                onChange={handlePinChange}
                inputStyle={{
                  border: '1px solid #FFC44D',
                  borderRadius: '8px',
                  margin: '10px 15px',
                }}
                ref={(n) => (ele = n)}
              />
            </Grid>
            <Typography sx={{ color: 'red' }}>{error}</Typography>
            <Grid container justifyContent='center' mt='10px'>
              {showPinConfirm ? (
                <>
                  <Grid xs={12} sx={{ m: '20px 0' }} textAlign={'center'}>
                    <Button
                      onClick={handleConfirmPin}
                      sx={{ textAlign: 'right' }}
                      disabled={disableConfirmButton}
                    >
                      Confirm
                    </Button>
                  </Grid>
                  <Grid>
                    <Button variant='text' onClick={handleBack} sx={{ textAlign: 'right' }}>
                      {' '}
                      Back{' '}
                    </Button>
                  </Grid>
                </>
              ) : (
                <Button
                  onClick={handleNext}
                  sx={{ textAlign: 'right' }}
                  disabled={disableNextButton}
                >
                  {' '}
                  Next{' '}
                </Button>
              )}
            </Grid>
          </Grid>
        </DialogContent>
      </StyledDialog>

      <StyledDialog open={showChangePasswordDialog} disableEnforceFocus>
        <DialogTitle id='simple-dialog-title' sx={{ padding: '0px 24px 0' }}>
          Change Password
        </DialogTitle>
        <DialogContent>
          <ChangePassword onCancel={() => setShowChangePasswordDialog(false)} />
        </DialogContent>
      </StyledDialog>
    </Container>
  );
};

export default Account;
